import React, { Component } from 'react';
import { Badge, Button, Col, Row } from 'reactstrap';
import { Redirect } from 'react-router-dom';

import DataTable, { PageHeader, EmptyState } from '../DataTable';
import FilterBuilder, { buildWhere } from '../FilterBuilder';
import { connect } from 'react-redux';
import { callApi, formatDate } from '../../utils';
import { showError } from '../../actions/notificationActions';
import { MINI_USER_FIELDS, isValidDomain } from './constants';

// ─── URL <-> filter state helpers (matches Users/List.js convention) ──────────

function encodeConditions(conditions) {
  return encodeURIComponent(
    JSON.stringify(conditions.map(({ id, ...rest }) => rest))
  );
}

function parseUrlParams(search) {
  const params = new URLSearchParams(search);
  let conditions = [];
  let logicalOp = '$and';

  const raw = params.get('c');
  if (raw) {
    try {
      const parsed = JSON.parse(decodeURIComponent(raw));
      conditions = parsed.map((c, i) => ({ ...c, id: Date.now() + i }));
    } catch (_) {}
  }

  const op = params.get('op');
  if (op === '$and' || op === '$or') logicalOp = op;

  return { conditions, logicalOp };
}

class DomainDetail extends Component {
  constructor(props) {
    super(props);
    const { conditions, logicalOp } = parseUrlParams(
      props.location ? props.location.search : ''
    );
    this.state = {
      users: [], // accumulated across cursor pages
      conditions,
      logicalOp,
      limit: 30,
      nextCursor: null, // keyset cursor for the next page; never URL-persisted
      hasMore: false,
      loading: false, // initial / reset load
      loadingMore: false, // appending the next page
      error: null,
      builderKey: 0,
    };
  }

  get domain() {
    return String(this.props.match.params.domain || '').toUpperCase();
  }

  componentDidMount() {
    if (isValidDomain(this.domain)) {
      this.loadUsers();
    }
  }

  componentDidUpdate(prevProps) {
    // Switching domains reuses this component instance — re-read the filter from
    // the new URL and reset paging (discard cursor, clear accumulated rows).
    const prevDomain = String(
      prevProps.match.params.domain || ''
    ).toUpperCase();
    if (prevDomain !== this.domain && isValidDomain(this.domain)) {
      const { conditions, logicalOp } = parseUrlParams(
        this.props.location ? this.props.location.search : ''
      );
      this.setState(
        (prev) => ({
          conditions,
          logicalOp,
          builderKey: prev.builderKey + 1,
        }),
        this.loadUsers
      );
    }
  }

  syncUrl = (conditions, logicalOp) => {
    const params = new URLSearchParams();
    if (conditions.length > 0) {
      params.set('c', encodeConditions(conditions));
      params.set('op', logicalOp);
    }

    const search = params.toString();
    const current = this.props.location
      ? this.props.location.search.replace(/^\?/, '')
      : '';
    if (search !== current) {
      this.props.history.replace({ search: search ? `?${search}` : '' });
    }
  };

  // Build the query string for a keyset page. The domain scope is enforced
  // server-side via the :domain path segment — it is intentionally NOT part of
  // the FilterBuilder `where`, so the user cannot remove it from the filter rows.
  buildQs = (cursor) => {
    const { conditions, logicalOp, limit } = this.state;
    const where = buildWhere(conditions, logicalOp);
    const hasFilter = Object.keys(where).length > 0;

    return [
      cursor != null ? `cursor=${encodeURIComponent(cursor)}` : '',
      `limit=${limit}`,
      hasFilter ? `where=${encodeURIComponent(JSON.stringify(where))}` : '',
    ]
      .filter(Boolean)
      .join('&');
  };

  // Initial / reset load: no cursor, replaces the accumulated rows.
  loadUsers = () => {
    this.setState({
      loading: true,
      error: null,
      users: [],
      nextCursor: null,
      hasMore: false,
    });
    callApi(`/domains/${this.domain}/users?${this.buildQs(null)}`)
      .then((res) => {
        const { data = [], meta = {} } = res || {};
        this.setState({
          loading: false,
          users: data,
          nextCursor: meta.next_cursor != null ? meta.next_cursor : null,
          hasMore: !!meta.has_more,
        });
        setTimeout(() => window.scrollTo(0, 0), 200);
      })
      .catch((err) => {
        this.setState({
          loading: false,
          error: (err && err.message) || 'Unable to load users.',
        });
        this.props.dispatch(showError(err));
      });
  };

  // Append the next keyset page using meta.next_cursor.
  loadMore = () => {
    const { nextCursor, hasMore, loadingMore } = this.state;
    if (loadingMore || !hasMore || nextCursor == null) return;

    this.setState({ loadingMore: true });
    callApi(`/domains/${this.domain}/users?${this.buildQs(nextCursor)}`)
      .then((res) => {
        const { data = [], meta = {} } = res || {};
        this.setState((prev) => ({
          loadingMore: false,
          users: prev.users.concat(data),
          nextCursor: meta.next_cursor != null ? meta.next_cursor : null,
          hasMore: !!meta.has_more,
        }));
      })
      .catch((err) => {
        // Keep the rows already loaded; just surface the error as a toast.
        this.setState({ loadingMore: false });
        this.props.dispatch(showError(err));
      });
  };

  handleFilterChange = (_where, conditions, logicalOp) => {
    this.setState({ conditions, logicalOp }, () => {
      this.syncUrl(conditions, logicalOp);
      this.loadUsers();
    });
  };

  clearFilters = () => {
    this.setState(
      (prev) => ({
        conditions: [],
        logicalOp: '$and',
        builderKey: prev.builderKey + 1,
      }),
      () => {
        this.syncUrl([], '$and');
        this.loadUsers();
      }
    );
  };

  openUser = (user) => {
    this.props.history.push(`/users/${user.id}`);
  };

  render() {
    // Guard against an invalid :domain in the URL.
    if (!isValidDomain(this.domain)) {
      return <Redirect to="/domains" />;
    }

    const {
      users,
      conditions,
      logicalOp,
      loading,
      loadingMore,
      hasMore,
      error,
      builderKey,
    } = this.state;
    const hasResults = users.length > 0;

    return (
      <div className="container-fluid">
        <Row className="mt-3 mt-md-4">
          <Col xs={12}>
            {/* ── Header ─────────────────────────────────────────────────────── */}
            <PageHeader
              beforeTitle={
                <Button
                  color="link"
                  size="sm"
                  style={{ padding: 0, marginBottom: 4 }}
                  onClick={() => this.props.history.push('/domains')}
                >
                  ← All domains
                </Button>
              }
              title={
                <React.Fragment>
                  <Badge color="info">{this.domain}</Badge> Users
                </React.Fragment>
              }
              subtitle={
                hasResults
                  ? `${users.length} user${
                      users.length === 1 ? '' : 's'
                    } loaded${hasMore ? ' — more available' : ''}`
                  : 'Users with access to this domain'
              }
            />

            {/* ── Mini FilterBuilder ─────────────────────────────────────────── */}
            <div style={{ marginBottom: 16 }}>
              <FilterBuilder
                key={builderKey}
                fields={MINI_USER_FIELDS}
                initialConditions={conditions}
                initialLogicalOp={logicalOp}
                onChange={this.handleFilterChange}
              />
            </div>

            {/* ── Results ────────────────────────────────────────────────────── */}
            <DataTable
              minWidth={640}
              loading={loading}
              footer={
                hasResults && !loading && hasMore ? (
                  <div style={{ textAlign: 'center' }}>
                    <Button
                      color="primary"
                      outline
                      size="sm"
                      onClick={this.loadMore}
                      disabled={loadingMore}
                    >
                      {loadingMore ? 'Loading…' : 'Load more'}
                    </Button>
                  </div>
                ) : null
              }
            >
              <thead className="text-primary">
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Blocked</th>
                  <th>Compliance</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>

              {!loading && error ? (
                <EmptyState
                  colSpan={9}
                  icon={false}
                  heading="Something went wrong"
                  message={error}
                  actionLabel="Retry"
                  onAction={this.loadUsers}
                />
              ) : !loading && !hasResults ? (
                <EmptyState
                  colSpan={9}
                  icon={false}
                  heading="No users in this domain match your filters"
                  message="Try adjusting or removing one of the conditions above."
                  actionLabel="Clear filters"
                  onAction={this.clearFilters}
                />
              ) : (
                <tbody>
                  {users.map((u, i) => (
                    <tr
                      key={u.id}
                      onClick={() => this.openUser(u)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{i + 1}</td>
                      <td>{u.name}</td>
                      <td
                        style={{
                          maxWidth: 180,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {u.email}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{u.phone}</td>
                      <td>
                        {Number(u.user_status) ? (
                          <Badge color="success">Active</Badge>
                        ) : (
                          <Badge color="secondary">Inactive</Badge>
                        )}
                      </td>
                      <td>
                        {u.is_blocked ? (
                          <Badge color="danger">Blocked</Badge>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{u.compliance != null ? u.compliance : '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {formatDate(u.created_at)}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <Button
                          color="link"
                          size="sm"
                          style={{ padding: 0 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            this.openUser(u);
                          }}
                        >
                          View user
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </DataTable>
          </Col>
        </Row>
      </div>
    );
  }
}

export default connect()(DomainDetail);
